import { useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Pencil, Trash2, Printer } from "lucide-react";
import { toast } from "sonner";
import { tl, itemPairs, itemAmount, orderTotals } from "@/lib/fair";

export const FAIR_STATUS = {
  pending: { label: "Bekliyor", cls: "text-[#1D4ED8] border-[#3B82F6]/40 bg-[#3B82F6]/10" },
  converted: { label: "Gerçekleşti", cls: "text-[#047857] border-[#047857]/40 bg-[#047857]/10" },
  cancelled: { label: "İptal", cls: "text-[#B91C1C] border-[#EF4444]/40 bg-[#EF4444]/10" },
};

export default function FairOrderRow({ o, productById, onEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const total = o.items.reduce((a, b) => a + b.koli, 0);

  const setStatus = async (status) => {
    try { await api.put(`/fair/orders/${o.id}/status`, { status }); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };
  const remove = async () => {
    if (!window.confirm("Fuar siparişi silinsin mi?")) return;
    await api.delete(`/fair/orders/${o.id}`); toast.success("Silindi"); onChanged();
  };

  return (
    <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-4" data-testid={`fair-order-${o.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-lg font-bold uppercase tracking-tight">{o.customer_name}</h3>
          <p className="text-[11px] font-mono text-[#8C7B68] uppercase tracking-widest mt-1">
            {new Date(o.created_at).toLocaleDateString("tr-TR")} · {o.items.length} renk{o.note ? ` · ${o.note}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-[10px] font-mono uppercase text-[#6B5B4A] tracking-widest">Toplam</p>
            <p className="font-mono text-lg font-bold" data-testid={`fair-order-total-${o.id}`}>{total} koli</p>
            <p className="font-mono text-xs text-[#B45309]" data-testid={`fair-order-amount-${o.id}`}>{tl(orderTotals(o).grand)} <span className="text-[#8C7B68]">KDV dahil</span></p>
          </div>
          <Select value={o.status} onValueChange={setStatus}>
            <SelectTrigger data-testid={`fair-order-status-${o.id}`} className={`h-9 w-36 text-xs font-mono uppercase border ${FAIR_STATUS[o.status]?.cls}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118]">
              {Object.entries(FAIR_STATUS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`fair-status-option-${k}`}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={() => setOpen(!open)} data-testid={`fair-order-toggle-${o.id}`} className="text-[#6B5B4A]">
            {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}</button>
          <Link to={`/fuar-fis/${o.id}`} target="_blank" data-testid={`fair-order-print-${o.id}`} title="Fiş Yazdır"
            className="text-[#8C7B68] hover:text-[#B45309]"><Printer className="w-4 h-4" /></Link>
          <button onClick={() => onEdit(o)} data-testid={`fair-order-edit-${o.id}`} className="text-[#8C7B68] hover:text-[#B45309]"><Pencil className="w-4 h-4" /></button>
          <button onClick={remove} data-testid={`fair-order-delete-${o.id}`} className="text-[#8C7B68] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {open && (
        <table className="w-full text-sm mt-4 border-t border-[#E8DDCA]">
          <thead><tr className="text-left text-[11px] font-mono uppercase text-[#6B5B4A]">
            <th className="py-2">Model</th><th className="py-2">Renk</th><th className="py-2 text-right">Koli</th>
            <th className="py-2 text-right">Çift</th><th className="py-2 text-right">Çift Fiyatı</th><th className="py-2 text-right">Tutar</th></tr></thead>
          <tbody>
            {o.items.map((it) => {
              const p = productById(it.fair_product_id);
              return (
                <tr key={it.fair_product_id} className="border-t border-[#E8DDCA]">
                  <td className="py-2 font-mono text-[#B45309]">{p?.model_code || "—"}</td>
                  <td className="py-2">{p?.model_color || "—"}</td>
                  <td className="py-2 text-right font-mono font-bold">{it.koli}</td>
                  <td className="py-2 text-right font-mono">{itemPairs(it)}</td>
                  <td className="py-2 text-right font-mono">{tl(it.price)}</td>
                  <td className="py-2 text-right font-mono font-bold">{tl(itemAmount(it))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
